const WHATSAPP_STICKER_CACHE_TTL_MS = 5 * 60 * 1000;
const WHATSAPP_STICKER_PAGE_LIMIT = 24;

export default {
  data() {
    return {
      isFetchingStickers: false,
      isLoadingMoreStickers: false,
      stickerCacheByKey: {},
      stickerFetchedAtByWaId: {},
      stickerPagination: {
        hasMore: false,
        limit: WHATSAPP_STICKER_PAGE_LIMIT,
        offset: 0,
        total: 0,
      },
      stickerPickerVisible: false,
      stickerPreviewUrls: {},
      stickerSearch: "",
      stickerSearchDebounceTimeout: null,
      whatsappStickerPickerStickers: [],
    };
  },
  beforeDestroy() {
    const component = this as any;
    clearTimeout(component.stickerSearchDebounceTimeout);
  },
  watch: {
    stickerSearch() {
      const component = this as any;
      clearTimeout(component.stickerSearchDebounceTimeout);
      component.stickerSearchDebounceTimeout = setTimeout(() => {
        component.fetchWhatsappStickers();
      }, 300);
    },
  },
  methods: {
    arrayBufferToBase64(bytes: Record<string, number>) {
      const arr = Object.keys(bytes).map((key) => bytes[key]);
      const bin = arr.reduce((acc, curr) => `${acc}${String.fromCharCode(curr)}`, "");
      return btoa(bin);
    },
    getStickerCacheKey() {
      const component = this as any;
      return `${component.selectedWhatsappWaId}:${component.stickerSearch.trim()}`;
    },
    fetchWhatsappStickers() {
      const component = this as any;

      if (!component.selectedWhatsappWaId) {
        component.whatsappStickerPickerStickers = [];
        component.stickerPagination = {
          ...component.stickerPagination,
          hasMore: false,
          offset: 0,
          total: 0,
        };
        return;
      }

      const now = Date.now();
      const stickerCacheKey = component.getStickerCacheKey();
      const lastFetchedAt = component.stickerFetchedAtByWaId[stickerCacheKey] || 0;
      const cachedStickers = component.stickerCacheByKey[stickerCacheKey];

      if (cachedStickers && now - lastFetchedAt < WHATSAPP_STICKER_CACHE_TTL_MS) {
        component.whatsappStickerPickerStickers = cachedStickers.stickers;
        component.stickerPagination = cachedStickers.pagination;
        component.whatsappStickerPickerStickers.forEach((sticker: any) =>
          component.fetchStickerPreview(sticker)
        );
        return;
      }

      component.isFetchingStickers = true;
      component.$store
        .dispatch("WHATSAPP_GET_STICKERS", {
          skipCommit: true,
          data: {
            limit: component.stickerPagination.limit,
            offset: 0,
            wa_id: component.selectedWhatsappWaId,
            search: component.stickerSearch.trim(),
            status: "active",
          },
        })
        .then((result: any) => {
          const stickers = result.stickers || [];
          const pagination = {
            hasMore: result.hasMore,
            limit: result.limit || component.stickerPagination.limit,
            offset: (result.offset || 0) + stickers.length,
            total: result.total,
          };

          component.whatsappStickerPickerStickers = stickers;
          component.stickerPagination = pagination;
          component.$set(component.stickerCacheByKey, stickerCacheKey, {
            pagination,
            stickers,
          });
          component.$set(component.stickerFetchedAtByWaId, stickerCacheKey, now);
          stickers.forEach((sticker: any) => component.fetchStickerPreview(sticker));
        })
        .catch(() => {
          component.$message.error("Failed to fetch WhatsApp stickers.");
        })
        .finally(() => {
          component.isFetchingStickers = false;
        });
    },
    loadMoreWhatsappStickers() {
      const component = this as any;

      if (
        !component.selectedWhatsappWaId ||
        component.isLoadingMoreStickers ||
        !component.stickerPagination.hasMore
      ) {
        return;
      }

      const stickerCacheKey = component.getStickerCacheKey();

      component.isLoadingMoreStickers = true;
      component.$store
        .dispatch("WHATSAPP_GET_STICKERS", {
          append: true,
          skipCommit: true,
          data: {
            limit: component.stickerPagination.limit,
            offset: component.stickerPagination.offset,
            wa_id: component.selectedWhatsappWaId,
            search: component.stickerSearch.trim(),
            status: "active",
          },
        })
        .then((result: any) => {
          const newStickers = result.stickers || [];
          const stickers = [...component.whatsappStickerPickerStickers, ...newStickers];
          const pagination = {
            hasMore: result.hasMore,
            limit: result.limit || component.stickerPagination.limit,
            offset: (result.offset || component.stickerPagination.offset) + newStickers.length,
            total: result.total,
          };

          component.whatsappStickerPickerStickers = stickers;
          component.stickerPagination = pagination;
          component.$set(component.stickerCacheByKey, stickerCacheKey, {
            pagination,
            stickers,
          });
          component.$set(component.stickerFetchedAtByWaId, stickerCacheKey, Date.now());
          newStickers.forEach((sticker: any) => component.fetchStickerPreview(sticker));
        })
        .catch(() => {
          component.$message.error("Failed to fetch WhatsApp stickers.");
        })
        .finally(() => {
          component.isLoadingMoreStickers = false;
        });
    },
    fetchStickerPreview(sticker: any) {
      const component = this as any;

      if (!sticker?.media_id || component.stickerPreviewUrls[sticker.id]) {
        return Promise.resolve(component.stickerPreviewUrls[sticker.id] || "");
      }

      return component.$store
        .dispatch("WHATSAPP_CLOUD_GET_MEDIA_BY_ID", {
          data: {
            endpoint: component.selectedWhatsappWaId,
            id: sticker.media_id,
          },
        })
        .then((res: any) => {
          const base64 = `data:${res.contentType};base64,${component.arrayBufferToBase64(
            res.data
          )}`;
          component.$set(component.stickerPreviewUrls, sticker.id, base64);
          return base64;
        })
        .catch(() => {
          component.$set(component.stickerPreviewUrls, sticker.id, "");
          return "";
        });
    },
  },
};
